import React from 'react';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';

/**
 * 長條圖組件
 * @param {Object} props
 * @param {Array<any>} props.data - 圖表資料
 * @param {string} props.xKey - X軸資料鍵名
 * @param {string} props.yKey - Y軸資料鍵名
 * @param {number} [props.height=300] - 圖表高度
 * @param {string} [props.color='#8b5cf6'] - 長條顏色
 * @param {string} [props.title] - 圖表標題
 * @param {boolean} [props.showGrid=true] - 是否顯示網格
 * @param {boolean} [props.showLegend=true] - 是否顯示圖例
 * @param {Array<string>} [props.colors] - 多色長條（每個長條不同顏色）
 */
function BarChart({
  data = [],
  xKey,
  yKey,
  height = 300,
  color = '#8b5cf6',
  title,
  showGrid = true,
  showLegend = true,
  colors = null
}) {
  // 預設顏色組合
  const defaultColors = [
    '#8b5cf6', // purple
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#ec4899', // pink
    '#6366f1', // indigo
    '#14b8a6', // teal
  ];

  // 自訂工具提示
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white px-4 py-3 rounded-lg shadow-lg border border-gray-200">
          <p className="text-sm font-medium text-gray-900 mb-1">{label}</p>
          <p className="text-sm font-semibold" style={{ color: payload[0].fill }}>
            {payload[0].name}: {payload[0].value}
          </p>
        </div>
      );
    }
    return null;
  };

  // 處理空資料
  if (!data || data.length === 0) {
    return (
      <div className="w-full bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-gray-500">無可用資料</p>
        </div>
      </div>
    );
  }

  // 驗證必要的鍵名
  if (!xKey || !yKey) {
    return (
      <div className="w-full bg-red-50 rounded-lg border-2 border-red-200 flex items-center justify-center p-4" style={{ height }}>
        <p className="text-red-600">錯誤: 必須提供 xKey 和 yKey 屬性</p>
      </div>
    );
  }

  // 決定使用的顏色
  const barColors = colors || defaultColors;

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={data}
          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
        >
          {showGrid && (
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          )}
          <XAxis
            dataKey={xKey}
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <YAxis
            stroke="#6b7280"
            style={{ fontSize: '12px' }}
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: '14px' }}
            />
          )}
          <Bar
            dataKey={yKey}
            fill={color}
            radius={[8, 8, 0, 0]}
            name={yKey}
          >
            {/* 如果提供了多色配置，為每個長條設置不同顏色 */}
            {colors && data.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={barColors[index % barColors.length]} />
            ))}
          </Bar>
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default BarChart;
